import { computed } from 'vue';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { METRICS, getMetrics } from '@/models/metrics.enum';
import {
  baseQueryParamMapping,
  defaultReportingPeriod,
} from '@/components/core/analytics/queryParamMapping';
import { searchBrandMediaTypes } from '@/app/library/constants';
import { SKEWED_IG_STORY_REACH_MESSAGE } from '@/config';
import { useFlagStore } from '@/stores/flag';

export const useYourPosts = () => {
  const metrics = getMetrics('yourPosts');

  const banners = computed(() => {
    const flagStore = useFlagStore();
    const bannerConfigs = [];
    const hasInstagramStoriesMetricUpdates = computed(
      () => flagStore.ready && flagStore.flags.igStoriesMetricUpdates,
    );

    if (!hasInstagramStoriesMetricUpdates.value) {
      bannerConfigs.push({
        severity: 'info',
        message: SKEWED_IG_STORY_REACH_MESSAGE,
        closeable: false,
      });
    }
    return bannerConfigs;
  });

  return {
    title: 'Instagram Story Frames',
    channelFilterKey: 'instagramStoryFrame',
    filters: {
      brandMediaTypes: [searchBrandMediaTypes.INSTAGRAM_STORY],
      default: {
        captionContain: [],
        captionNotContain: [],
        contentTags: [],
        contentTagMatchType: 'ANY',
        postType: [],
        containSwipeUpLink: undefined,
        reportingPeriod: defaultReportingPeriod,
        sort: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
      options: {
        mediaType: [
          { label: 'Photo', value: 'IMAGE' },
          { label: 'Video', value: 'VIDEO' },
        ],
      },
      queryParamMapping: {
        ...baseQueryParamMapping,
        captionContain: { queryParamName: 'captionContain', type: 'array', elementType: 'string' },
        captionNotContain: {
          queryParamName: 'captionNotContain',
          type: 'array',
          elementType: 'string',
        },
        postType: { queryParamName: 'mediaTypes', type: 'array', elementType: 'string' },
        containSwipeUpLink: { queryParamName: 'containSwipeUpLink', type: 'boolean' },
      },
      scope: 'instagramStoryFrames',
      sortDetails: {
        options: [
          { label: 'Recently Published', value: 'DATE' },
          {
            title: 'Organic',
            children: metrics.all([
              METRICS.INSTAGRAM_STORY.REACH,
              METRICS.INSTAGRAM_STORY.VIEWS,
              METRICS.INSTAGRAM_STORY.COMPLETION_RATE_VIEWS,
              METRICS.INSTAGRAM_STORY.EXIT_RATE_VIEWS,
              METRICS.INSTAGRAM_STORY.EXITS,
              METRICS.INSTAGRAM_STORY.LINK_CLICKS,
              METRICS.INSTAGRAM_STORY.REPLIES,
            ]),
          },
          {
            title: 'Discontinued',
            children: metrics.all([
              METRICS.INSTAGRAM_STORY.IMPRESSIONS,
              METRICS.INSTAGRAM_STORY.COMPLETION_RATE,
              METRICS.INSTAGRAM_STORY.EXIT_RATE,
            ]),
          },
        ],
        default: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
    },
    download: {
      pdf: {
        fileName: 'yourInstagramStoryFramesPDF',
        type: 'instagramStoryFrames',
        label: 'Your Instagram Story Frames PDF',
        pendingKey: 'instagramStoryFramesReportPending',
      },
      csv: {
        fileName: 'yourInstagramStoryFramesCSV',
        type: 'instagramStoryFrames',
        label: 'Your Instagram Story Frames CSV',
        pendingKey: 'instagramStoryFramesCSVPending',
      },
    },
    displayMetrics: metrics.all([
      METRICS.INSTAGRAM_STORY.REACH,
      METRICS.INSTAGRAM_STORY.VIEWS,
      METRICS.INSTAGRAM_STORY.COMPLETION_RATE_VIEWS,
      METRICS.INSTAGRAM_STORY.LINK_CLICKS,
    ]),
    metricLabels: metrics.all(Object.values(METRICS.INSTAGRAM_STORY)),
    mediaCardDefaults: {
      metrics: ['reach', 'viewsV2', 'completionRateViews', 'swipeUps'],
      labels: {
        reach: 'Reach',
        viewsV2: 'Views',
        completionRateViews: 'Completion Rate',
        swipeUps: 'Link Clicks',
      },
    },
    platformConnection: PLATFORM_CONNECTION.FACEBOOK,
    banners,
  };
};
